import React from 'react';
import { Row, Toggle } from './settingsPrimitives';
import EditableNumberPicker from './ui/EditableNumberPicker';
import { DEFAULT_WS_SETTINGS } from '../utils/liveConnections';

/** What the browser's own WebSocket cannot honour — shown, switched off, with the reason. */
const DESKTOP_ONLY = 'Needs the desktop app: a browser WebSocket does not offer this.';

/**
 * The Settings tab of a WebSocket request — the Settings modal's rows (`settingsPrimitives`)
 * so it reads like every other settings page. Applied at the next Connect.
 *
 * @param {{ settings: object, onChange: (next: object) => void, native: boolean, disabled?: boolean }} props
 *   `native`: the desktop client is in use (see `utils/websocketClientNative.js`)
 */
export default function WebSocketSettingsPanel({ settings, onChange, native, disabled = false }) {
  const value = { ...DEFAULT_WS_SETTINGS, ...(settings || {}) };
  const set = (key) => (next) => onChange({ ...value, [key]: next });
  const off = disabled || !native;
  const why = (text) => (native ? text : `${text} ${DESKTOP_ONLY}`);

  return (
    <div className="ws-settings panel-scroll" aria-label="WebSocket settings">
      <Row label="Verify the server certificate" desc={why('Turn off for a self-signed certificate on a development server.')}>
        <Toggle value={native ? value.verifyCertificate : true} onChange={set('verifyCertificate')} disabled={off} />
      </Row>
      <Row label="Handshake timeout" desc={why('How long connecting may take. 0 uses the default of 30 seconds.')}>
        <EditableNumberPicker
          value={Math.round(value.handshakeTimeoutMs / 1000)}
          onChange={(seconds) => set('handshakeTimeoutMs')(seconds * 1000)}
          presets={[0, 5, 10, 30, 60]} min={0} max={600} suffix="s" ariaLabel="Handshake timeout in seconds" disabled={off}
        />
      </Row>
      <Row label="Maximum message size" desc={why('A larger message closes the connection (1009). 0 uses the default of 64 MB.')}>
        <EditableNumberPicker
          value={value.maxMessageSizeMb}
          onChange={set('maxMessageSizeMb')}
          presets={[0, 1, 5, 16, 64]} min={0} max={1024} suffix="MB" ariaLabel="Maximum message size in megabytes" disabled={off}
        />
      </Row>
      <Row label="Reconnection attempts" desc="How often to try again after the connection drops. 0 never reconnects.">
        <EditableNumberPicker
          value={value.reconnectAttempts}
          onChange={set('reconnectAttempts')}
          presets={[0, 1, 3, 5, 10]} min={0} max={100} ariaLabel="Reconnection attempts" disabled={disabled}
        />
      </Row>
      <Row label="Reconnection interval" desc="The pause before each new attempt.">
        <EditableNumberPicker
          value={Math.round(value.reconnectIntervalMs / 1000)}
          onChange={(seconds) => set('reconnectIntervalMs')(seconds * 1000)}
          presets={[1, 3, 5, 10, 30]} min={1} max={600} suffix="s" ariaLabel="Reconnection interval in seconds" disabled={disabled}
        />
      </Row>
    </div>
  );
}
